import { createAsyncThunk } from "@reduxjs/toolkit";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import createApiClient from "../../utils/createApiClient";

export interface ProfileData {
  firstname?: string;
  lastname?: string;
  email: string;
  mobileNo?: string;
  emailNotification?: boolean;
  planName?: string;
  siteReportUsed?: number;
  siteReportRemaining?: number;
  fleetReportUsed?: number;
  fleetReportRemaining?: number;
  profileImg?: string;
  planExpiryDate?: string;
}

export interface ProfileApiResponse {
  success: boolean;
  message: string;
  data?: {
    user: ProfileData;
  };
}

export interface ChangePasswordRequest {
  currentPassword: string;
  newPassword: string;
}

export interface ChangePasswordApiResponse {
  success: boolean;
  message: string;
}

function isAxiosError(error: unknown): error is import("axios").AxiosError {
  return (error as import("axios").AxiosError).isAxiosError !== undefined;
}

// GET PROFILE DETAILS ACTION
export const getProfileDetails = createAsyncThunk<
  ProfileApiResponse,
  { id: string },
  { rejectValue: string }
>("profile/getProfileDetails", async ({ id }, { rejectWithValue }) => {
  try {
    const response = await createApiClient.get<ProfileApiResponse>(
      `/users/profile/${id}`
    );

    if (response.data.success) {
      return response.data;
    } else {
      toast.error(
        response.data.message || "Failed to retrieve profile details"
      );
      return rejectWithValue(
        response.data.message || "Failed to retrieve profile details"
      );
    }
  } catch (error) {
    let errorMessage = "An unknown error occurred";
    if (isAxiosError(error) && error.response) {
      errorMessage =
        (error.response.data as ProfileApiResponse).message || "Unknown error";
    } else if (error instanceof Error) {
      errorMessage = error.message;
    }
    toast.error(errorMessage);
    return rejectWithValue(errorMessage);
  }
});

// UPDATE PROFILE DETAILS ACTION
export const updateProfileDetails = createAsyncThunk<
  ProfileApiResponse,
  { id: string; formData: Partial<ProfileData> },
  { rejectValue: string }
>(
  "profile/updateProfileDetails",
  async ({ id, formData }, { rejectWithValue }) => {
    try {
      const response = await createApiClient.put<ProfileApiResponse>(
        `/users/profile/${id}`,
        formData
      );

      if (response.data.success) {
        toast.success(
          response.data.message || "Profile details updated successfully"
        );
        return response.data;
      } else {
        toast.error(
          response.data.message || "Failed to update profile details"
        );
        return rejectWithValue(
          response.data.message || "Failed to update profile details"
        );
      }
    } catch (error) {
      let errorMessage = "An unknown error occurred";
      if (isAxiosError(error) && error.response) {
        errorMessage =
          (error.response.data as ProfileApiResponse).message ||
          "Unknown error";
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }
      toast.error(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

// CHANGE PASSWORD ACTION
export const changePassword = createAsyncThunk<
  ChangePasswordApiResponse,
  ChangePasswordRequest,
  { rejectValue: string }
>(
  "profile/changePassword",
  async ({ currentPassword, newPassword }, { rejectWithValue }) => {
    try {
      const response = await createApiClient.put<ChangePasswordApiResponse>(
        `/auth/change-password`,
        { currentPassword, newPassword }
      );

      if (response.data.success) {
        toast.success(response.data.message || "Password changed successfully");
        return response.data;
      } else {
        toast.error(response.data.message || "Failed to change password");
        return rejectWithValue(
          response.data.message || "Failed to change password"
        );
      }
    } catch (error) {
      let errorMessage = "An unknown error occurred";
      if (isAxiosError(error) && error.response) {
        errorMessage =
          (error.response.data as ChangePasswordApiResponse).message ||
          "Unknown error";
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }
      toast.error(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);
